import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../constants/api";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { capitalize } from "../../../utils/helpers";
import BulkUploadModal from "./BulkUploadModal";
import BackBtn from '../../../assets/svg/backbtn.svg';
import { RootState } from "../../../store";

interface Question {
    _id: string;
    question: string;
    question_number: number;
    options: string[];
    correct_option_id: number;
}

export default function QuizQuestions() {
    const { folder, subfolder } = useParams<{ folder: string; subfolder: string }>();
    const navigate = useNavigate();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string | number>(currentPage);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const pageSize = 10; // Number of questions per page

    // Retrieve token from Redux store
    const token = useSelector((state: RootState) => state.account.token);

    // Fetch questions from the API
    const fetchQuestions = useCallback(async (page: number) => {
        setLoading(true);
        try {
            const topic = capitalize(folder || "");
            const subtopic = capitalize(subfolder || "");
            const response = await fetch(
                `${baseUrl}/api/questions/get-all-questions?page=${page}&limit=${pageSize}&topic_name=${topic}&subtopic_name=${subtopic}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.ok) {
                const result = await response.json();
                setQuestions(result.questions);
                setTotalPages(result.pagination.totalPages);
            } else {
                console.error("Failed to fetch questions");
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
        } finally {
            setLoading(false);
        }
    }, [folder, subfolder, token]);

    useEffect(() => {
        fetchQuestions(currentPage);
    }, [fetchQuestions, currentPage]);

    const handleAddQuestion = () => {
        navigate(`/ques-folders/${folder}/${subfolder}/add-question`);
    };

    const handleEditQuestion = (questionId: string) => {
        navigate(`/ques-folders/${folder}/${subfolder}/edit-question/${questionId}`);
    };

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handlePageUpdate = () => {
        const pageNumber = parseInt(inputValue.toString(), 10);
        if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        } else {
            setInputValue(currentPage);
        }
    };

    const handlePageInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handlePageUpdate();
        }
    };

    const openConfirmationDialog = (questionId: string) => {
        setIsConfirming(true);
        setQuestionToDelete(questionId);
    };

    const closeConfirmationDialog = () => {
        setIsConfirming(false);
        setQuestionToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!questionToDelete) return;
        try {
            const response = await fetch(`${baseUrl}/api/questions/delete-ques/${questionToDelete}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                setQuestions((prevQuestions) =>
                    prevQuestions.filter((q) => q._id !== questionToDelete)
                );
            } else {
                console.error("Failed to delete the question");
            }
        } catch (error) {
            console.error("Error deleting the question:", error);
        } finally {
            setIsConfirming(false);
            setQuestionToDelete(null);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    useEffect(() => {
        setInputValue(currentPage);
    }, [currentPage]);

    if (loading) {
        return (
            <div className="quiz-questions-container">
                <p className="loading-container">Loading...</p>
            </div>
        );
    }

    return (
        <>
            <div className="quiz-questions-container">
                <div className="header-container">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div onClick={() => navigate('/ques-folders', { state: { openedFolder: folder } })} style={{ cursor: 'pointer', marginRight: '20px' }}>
                            <img src={BackBtn} alt="Back Button" />
                        </div>
                        <h2 style={{ margin: 0 }}>Questions for {capitalize(subfolder || "")}</h2>
                    </div>
                    <div className="header-actions">
                        <button
                            className="bulk-upload-button"
                            onClick={() => setShowBulkUpload(true)}
                        >
                            📤 Bulk Upload
                        </button>
                        <button className="add-question-button" onClick={handleAddQuestion}>
                            + Add Question
                        </button>
                    </div>
                </div>

                <div className="questions-list">
                    <div className="quiz-cards">
                        {questions.map((question) => (
                            <div className="quiz-card" key={question._id}>
                                <div className="quiz-question-row">
                                    <p className="quiz-question">
                                        {question.question_number}. {question.question}
                                    </p>
                                    <div className="quiz-header-actions">
                                        <button
                                            className="icon-btn edit-btn"
                                            title="Edit question"
                                            onClick={() => handleEditQuestion(question._id)}
                                        >
                                            <AiFillEdit />
                                        </button>
                                        <button
                                            className="icon-btn delete-btn"
                                            title="Delete question"
                                            onClick={() => openConfirmationDialog(question._id)}
                                        >
                                            <AiFillDelete />
                                        </button>
                                    </div>
                                </div>
                                <div className="quiz-options">
                                    {question.options.map((option, i) => (
                                        <div
                                            key={i}
                                            className={`quiz-option ${i === question.correct_option_id ? "correct-option" : ""}`}
                                        >
                                            <span className="option-letter">{String.fromCharCode(97 + i)}.</span>
                                            {option}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    {questions.length === 0 && <p className="no-questions">No Questions Found.</p>}

                    {questions.length > 0 && (
                        <div className="pagination-controls">
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            <span className="pagination-info">
                                Page &nbsp;
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={handlePageInputChange}
                                    onBlur={handlePageUpdate}
                                    onKeyDown={handlePageInputKeyDown}
                                    className="pagination-input"
                                /> of {totalPages}
                            </span>
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {isConfirming && (
                        <div className="confirmation-overlay">
                            <div className="confirmation-window">
                                <p>Are you sure you want to delete this question?</p>
                                <div className="confirmation-buttons">
                                    <button
                                        className="confirmation-button cancel"
                                        onClick={closeConfirmationDialog}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="confirmation-button"
                                        onClick={handleConfirmDelete}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showBulkUpload && (
                <BulkUploadModal
                    folder={capitalize(folder || "")}
                    subfolder={capitalize(subfolder || "")}
                    onClose={() => setShowBulkUpload(false)}
                    onSuccess={() => {
                        setShowBulkUpload(false);
                        fetchQuestions(currentPage);
                    }}
                />
            )}
        </>
    );
}
